import { match } from 'ts-pattern'
import { pluralize } from '../../../utils/text-utils.ts'
import * as styles from './beacon-coverage.css.ts'
import { BEACON_DISMISSED_LEADS_ID } from './beacon-dismissed-leads.tsx'
import { checkLabel, type CoverageModel, formatTokens, unlistedDismissedCount } from './coverage-model.ts'

/** The proof behind the verdict: files read, checks run, how suspected weaknesses became findings, tokens by check. */
export const BeaconCoverageEvidence: React.FC<{ model: CoverageModel }> = ({ model }) => {
  const proof = model.summary.coverage.proof
  const verified = model.summary.counts.findings_kept
  const listed = model.summary.dismissed.length
  const unlisted = unlistedDismissedCount(model.summary)
  const maxSpent = Math.max(1, ...proof.lenses.map(lens => lens.spent))
  const kindsOfCheck = new Set(
    proof.lenses.filter(lens => lens.run && lens.lens !== 'ai_scan_verify').map(lens => lens.lens)
  ).size
  const funnel: readonly [number, string][] = [
    [proof.candidates_raw, 'suspected weaknesses raised'],
    [proof.candidates_deduped, 'after merging duplicates'],
    [verified, 'verified findings'],
  ]
  return (
    <section className={styles.evidence} aria-label="Evidence">
      <div className={styles.evidenceCell}>
        <span className={styles.evidenceKey}>Files read</span>
        <span className={styles.evidenceValue}>{proof.files_read}</span>
        <p className={styles.evidenceNote}>distinct files Beacon read</p>
        {proof.files_read_sample.length > 0 && (
          <details>
            <summary className={styles.filesSummary}>
              Show {proof.files_read_sample.length} of {proof.files_read}
            </summary>
            <ul className={styles.filesList}>
              {proof.files_read_sample.map(file => (
                <li key={file}>{file}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
      <div className={styles.evidenceCell}>
        <span className={styles.evidenceKey}>Checks run</span>
        <span className={styles.evidenceValue}>
          {proof.investigations_run}
          <span className={styles.evidenceOf}> / {proof.investigations_planned}</span>
        </span>
        <p className={styles.evidenceNote}>
          run of planned, across {kindsOfCheck} kinds of check; {proof.operations_mapped} operations mapped
        </p>
      </div>
      <div className={styles.evidenceCell}>
        <span className={styles.evidenceKey}>Findings</span>
        <div className={styles.funnel}>
          {funnel.map(([count, label], index) => (
            <div key={label}>
              <div className={styles.funnelRow}>
                <i
                  className={styles.funnelBar({ isKept: index === funnel.length - 1 })}
                  style={{ width: `${proof.candidates_raw ? (count / proof.candidates_raw) * 100 : 0}%` }}
                />
                <b className={styles.funnelCount}>{count}</b>
              </div>
              <span className={styles.evidenceNote}>{label}</span>
            </div>
          ))}
        </div>
        <p className={styles.evidenceNote}>
          {match({ listed, unlisted })
            .with({ listed: 0, unlisted: 0 }, () => 'No leads dismissed')
            .with({ listed: 0 }, () => (
              <>
                {unlisted} {pluralize(unlisted, 'lead', 'leads')} dismissed; this run’s summary does not list them
              </>
            ))
            .otherwise(() => (
              <>
                {listed} {pluralize(listed, 'lead', 'leads')} dismissed ·{' '}
                <a href={`#${BEACON_DISMISSED_LEADS_ID}`} className={styles.inlineLink}>
                  See why
                </a>
                {unlisted > 0 && `; ${unlisted} more not listed in this run’s summary`}
              </>
            ))}
        </p>
      </div>
      <div className={styles.evidenceCell}>
        <span className={styles.evidenceKey}>
          Tokens · {formatTokens(proof.tokens_spent)} of {formatTokens(proof.token_budget)}
        </span>
        <div className={styles.lensBars}>
          {proof.lenses.map(lens => (
            <div key={lens.lens} className={styles.lensBar}>
              <span>{checkLabel(lens.lens)}</span>
              <span className={styles.lensTrack({ isUnrun: lens.planned === 0 })}>
                <i className={styles.lensFill} style={{ width: `${(lens.spent / maxSpent) * 100}%` }} />
              </span>
              <span className={styles.lensSpent}>{lens.planned === 0 ? 'none' : formatTokens(lens.spent)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
